import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api/apiClient";
import { handleErrors } from "../modules/handleErrors";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import WarningModal from "../modals/WarningModal";
import AddEditSectionModal from "./AddEditSectionModal";

const FeaturedSections = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);

  const fetchSections = async () => {
    try {
      setLoader(true);
      const result = await apiClient.get("/featured-sections/");
      setLoader(false);
      if (!result.ok) {
        handleErrors(result.data);
        return;
      }
      setSections(result.data?.results ?? result.data ?? []);
    } catch (error) {
      setLoader(false);
      console.error("Error fetching featured sections:", error);
      toast.error("Failed to load featured sections");
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const handleAddSection = () => {
    setEditingSection(null);
    setShowModal(true);
  };

  const handleEditSection = (e, section) => {
    e.stopPropagation();
    setEditingSection(section);
    setShowModal(true);
  };

  const handleDeleteClick = (e, section) => {
    e.stopPropagation();
    setSectionToDelete(section);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!sectionToDelete?.id) return;
    try {
      setDeleteLoader(true);
      const result = await apiClient.delete(`/featured-sections/${sectionToDelete.id}/`);
      setDeleteLoader(false);
      if (!result.ok) {
        handleErrors(result.data);
        return;
      }
      toast.success("Section deleted successfully");
      setShowDeleteConfirm(false);
      setSectionToDelete(null);
      fetchSections();
    } catch (error) {
      setDeleteLoader(false);
      toast.error("Failed to delete section");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingSection(null);
  };

  const handleSectionSaved = () => {
    handleModalClose();
    fetchSections();
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <h2 className="mb-0" style={{ color: "#000000e6" }}>
          Featured Sections
        </h2>
        <button
          className="btn text-light"
          style={{ backgroundColor: "#C93312" }}
          onClick={handleAddSection}
        >
          Add Section
        </button>
      </div>

      {loader ? (
        <div className="d-flex justify-content-center mt-5">
          <LoadingSpinner color="#FF3E1F" />
        </div>
      ) : sections.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <p>No featured sections yet.</p>
          <p>Click &quot;Add Section&quot; to create one with a name, description, and mockups.</p>
        </div>
      ) : (
        <div className="row">
          {sections.map((section) => (
            <div
              key={section.id}
              className="col-md-6 col-lg-4 mb-4"
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/admin/featured-sections/${section.id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/admin/featured-sections/${section.id}`);
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <div className="card shadow h-100 border-0 rounded-3 overflow-hidden">
                <div className="card-body">
                  <h6 className="card-title fw-bold text-truncate" title={section.name}>
                    {section.name}
                  </h6>
                  <p
                    className="card-text text-muted small mb-2 text-start"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                    title={section.description}
                  >
                    {section.description || "—"}
                  </p>
                  <small className="text-muted">
                    {section?.mockups_count}{" "}
                    mockup(s)
                  </small>
                  <div className="d-flex items-center justify-center gap-2 mt-3">
                    {/* <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => handleEditSection(e, section)}
                    >
                      Edit
                    </button> */}
                    <button
                className="btn d-none d-lg-block"
                style={{
                  backgroundColor: "#0e0e0e",
                  color: "#fff",
                  border: "none",
                  fontWeight: "600",
                  padding: "6px 32px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "15px",
                  textDecoration: "none",
                }}
                onClick={(e) => handleEditSection(e, section)}
                >
                Edit
              </button>
               
                    <button
                className="btn d-none d-lg-block"
                style={{
                  backgroundColor: "#c93312",
                  color: "#fff",
                  border: "none",
                  fontWeight: "600",
                  padding: "6px 24px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  fontSize: "15px",
                  textDecoration: "none",
                }}
                onClick={(e) => handleDeleteClick(e, section)}
                >
                Delete
              </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddEditSectionModal
        show={showModal}
        onHide={handleModalClose}
        onSaved={handleSectionSaved}
        section={editingSection}
      />

      <WarningModal
        showModal={showDeleteConfirm}
        modalClose={() => {
          if (!deleteLoader) {
            setShowDeleteConfirm(false);
            setSectionToDelete(null);
          }
        }}
        setConfirmClose={confirmDelete}
        text={`Are you sure you want to delete "${sectionToDelete?.name}"?`}
      />
    </div>
  );
};

export default FeaturedSections;
